/**
 * Scope-switcher data + selection logic (V2-FE-1, re-architected
 * V2-FE-switcher-list-employers).
 *
 * Drives the hierarchical scope switcher in the top bar (`OrgChip.vue`):
 *  - fetches **every** employer under the tenant via the single
 *    `GET /employment-svc/v2/employers` endpoint (`listEmployers`), then groups
 *    the flat list **in-app** by `Type` / `Kind` / `ParentId` (the previous
 *    by-Kind `scope-tree` endpoint is retired);
 *  - applies the role + `administered_companies` visibility filter;
 *  - exposes the grouped, indented structure the switcher renders + the
 *    flattened, ordered list of selectable employers;
 *  - owns the **default-pick** rule: tenant admins default to **All Scopes**
 *    (global); everyone else to the **first administered company**;
 *  - resolves the tenant's `TENANT_EMPLOYER` id into the auth store so that —
 *    under global scope — per-module data still binds to a concrete employer
 *    (the tenant company) rather than to `null`;
 *  - on every scope change, **invalidates all module queries** so Home /
 *    People / Organization / HR Ops / … reload bound to the new scope, and
 *    warm-prefetches the visited-but-unmounted screens.
 *
 * The employer-list query is keyed by the tenant (the JWT's `tenant_id`), not
 * the active scope — switching scope must NOT refetch the list.
 */
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { listEmployers, type Employer } from '@/api/services/employers';
import {
  isTenantAdmin,
  parseAdministeredCompanies,
  useAuthStore,
  type ActiveScope,
  type JwtClaims,
} from '@/stores/auth';
import { useVisitedRoutesStore } from '@/stores/visitedRoutes';

const EMPTY_EMPLOYERS: Employer[] = [];

/**
 * The query keys that must be re-run when the active scope changes. Each
 * module's read query key is prefixed with one of these roots and includes
 * `activeEmployerId`, so invalidating the prefix forces a refetch bound to the
 * new scope. New modules add their key root here.
 */
export const SCOPE_BOUND_QUERY_ROOTS = [
  'people',
  'employment-svc',
  'organization',
  'clients',
  'projects',
  'pay',
  'global-aggregate',
] as const;

/**
 * Invalidate every scope-bound module query. Exported as a pure helper so the
 * switcher (and tests) can trigger a tenant-wide rebind without mounting the
 * full composable graph.
 */
export function invalidateScopeBoundQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  for (const root of SCOPE_BOUND_QUERY_ROOTS) {
    queryClient.invalidateQueries({ queryKey: [root] });
  }
}

/**
 * Maps a visited module route name to the scope-bound query roots its screen
 * reads. Used to lazily warm-prefetch already-visited (but currently unmounted)
 * screens after a scope change so switching back is instant. Only roots that a
 * route actually queries are listed — over-prefetching wastes RCUs.
 */
const ROUTE_QUERY_ROOTS: Record<string, readonly (typeof SCOPE_BOUND_QUERY_ROOTS)[number][]> = {
  home: ['global-aggregate'],
  people: ['people', 'employment-svc'],
  organization: ['organization', 'employment-svc', 'clients', 'projects'],
  pay: ['pay'],
};

/**
 * Background-warm the already-visited (but currently unmounted) screens after a
 * scope change. The active screen's query refetches automatically via
 * `invalidateScopeBoundQueries` (its observer is mounted); this re-runs the
 * cached query functions for *inactive* queries on visited routes so a
 * subsequent navigation back renders from a warm cache instead of a skeleton.
 *
 * Cache-friendly by construction: `refetchQueries({ type: 'inactive' })` only
 * touches queries whose `queryFn` is already in the cache, so it never fires a
 * request for a screen the user has never opened, and `keepPreviousData` keeps
 * the prior data visible until the warm refetch resolves.
 */
export function prefetchVisitedScopes(
  queryClient: ReturnType<typeof useQueryClient>,
  visitedRouteNames: string[],
): void {
  const roots = new Set<string>();
  for (const name of visitedRouteNames) {
    for (const root of ROUTE_QUERY_ROOTS[name] ?? []) roots.add(root);
  }
  for (const root of roots) {
    void queryClient.refetchQueries({ queryKey: [root], type: 'inactive' });
  }
}

// ---------------------------------------------------------------------------
// In-app grouping (pure helpers — the previous by-Kind endpoint moved here)
// ---------------------------------------------------------------------------

/** A counterparty (client / vendor) employer + its matched OS_PROJECT children. */
export interface CounterpartyEntry {
  employer: Employer;
  projects: Employer[];
}

/**
 * A company (the tenant employer or a managed COMPANY subsidiary) together with
 * the counterparties whose `ParentId` points at it. Lets the switcher place each
 * client / vendor under the company that actually owns it, rather than lumping
 * them all under the tenant.
 */
export interface CompanyNode {
  employer: Employer;
  clients: CounterpartyEntry[];
  vendors: CounterpartyEntry[];
}

/**
 * The switcher's grouped, indented structure, derived from the flat employer
 * list + claims:
 *  - `tenantEmployer` — the `TENANT_EMPLOYER` company, when it is visible to the
 *    principal (rendered at the top indent level);
 *  - `companies` — the managed `COMPANY` employers (the tenant employer is NOT
 *    repeated here); indented under the tenant employer when it is shown,
 *    rendered flat otherwise (see `companiesIndented`);
 *  - `clients` — the visible `OS_CLIENT` counterparties, each carrying its
 *    projects (matched by `ParentId`); empty when none are visible;
 *  - `vendors` — the visible `OS_VENDOR` counterparties, same shape;
 *  - `looseProjects` — administered `OS_PROJECT` employers whose parent isn't
 *    itself visible; rendered flat, like `companies`.
 *
 * Both `clients` and `vendors` are surfaced regardless of tenant persona — a
 * provider tenant can also be a client to some vendors. The switcher hides a
 * group when its array is empty (owner decision "Option A").
 */
export interface ScopeListGroups {
  tenantEmployer: Employer | null;
  companies: Employer[];
  /** `true` when companies should render indented beneath `tenantEmployer`. */
  companiesIndented: boolean;
  clients: CounterpartyEntry[];
  vendors: CounterpartyEntry[];
  /**
   * Each managed COMPANY + the counterparties parented to it (placement honours
   * `OS_CLIENT`/`OS_VENDOR` `ParentId`). Companies that own counterparties sort
   * first so they stay above the "Show more" fold.
   */
  subsidiaryNodes: CompanyNode[];
  /** Counterparties parented to the tenant employer (or orphaned → shown there). */
  tenantClients: CounterpartyEntry[];
  tenantVendors: CounterpartyEntry[];
  /**
   * Administered `OS_PROJECT` employers whose parent client/vendor isn't
   * itself visible (a project-scoped admin, or a legacy project row that
   * predates `ParentId` being backfilled) — rendered flat, like `companies`,
   * since there's no visible parent to nest them under.
   */
  looseProjects: Employer[];
}

const EMPTY_GROUPS: ScopeListGroups = {
  tenantEmployer: null,
  companies: [],
  companiesIndented: false,
  clients: [],
  vendors: [],
  subsidiaryNodes: [],
  tenantClients: [],
  tenantVendors: [],
  looseProjects: [],
};

/** `true` when an employer is a plain company (Kind=COMPANY; null → COMPANY). */
function isCompanyKind(e: Employer): boolean {
  return e.kind === 'COMPANY';
}

/**
 * Find the tenant's main `TENANT_EMPLOYER` company in the flat list. There is
 * at most one; `null` when the list has none (e.g. a managed-only sub-tenant).
 */
export function findTenantEmployer(employers: Employer[]): Employer | null {
  return employers.find((e) => e.type === 'TENANT_EMPLOYER') ?? null;
}

/**
 * Derive the switcher's grouped structure from the flat employer list, applying
 * the role + `administered_companies` visibility rule:
 *  - **TenantAdmin** sees every employer (the global option is handled
 *    separately) — supersedes `administered_companies`.
 *  - **non-admin** sees only employers whose id is in `administered_companies`.
 *    With an empty list, they see nothing (the switcher shows its empty state).
 *
 * Grouping:
 *  1. The `TENANT_EMPLOYER` is surfaced as `tenantEmployer` when visible.
 *  2. Managed `COMPANY` employers fill `companies`; they indent under the
 *     tenant employer when it is shown (`companiesIndented`), else render flat.
 *  3. BOTH Clients (`OS_CLIENT`) and Vendors (`OS_VENDOR`) form their own
 *     groups, surfaced regardless of tenant persona (owner decision "Option
 *     A" — a provider tenant can also be a client to some vendors). Projects
 *     (`OS_PROJECT`) nest under their parent counterparty by `ParentId`. Each
 *     group is empty (`[]`) when no entries are visible; the switcher hides
 *     empty groups.
 */
export function deriveScopeListGroups(
  employers: Employer[],
  claims: JwtClaims | null,
): ScopeListGroups {
  if (employers.length === 0) return EMPTY_GROUPS;

  const admin = isTenantAdmin(claims);
  const administered = new Set(admin ? [] : parseAdministeredCompanies(claims));
  const visible = (e: Employer): boolean => admin || administered.has(e.id);

  // 1. Tenant employer (at most one) — shown when visible to the principal.
  const tenantEmployerRaw = findTenantEmployer(employers);
  const tenantEmployer = tenantEmployerRaw && visible(tenantEmployerRaw) ? tenantEmployerRaw : null;

  // 2. Managed companies (Kind=COMPANY) excluding the tenant employer itself.
  const companies: Employer[] = [];
  for (const e of employers) {
    if (e.type === 'TENANT_EMPLOYER') continue;
    if (!isCompanyKind(e)) continue;
    if (!visible(e)) continue;
    companies.push(e);
  }

  // Projects matched to their parent counterparty by ParentId, when that
  // parent is itself visible. A principal who administers a single project
  // directly (their id is the one listed in `administered_companies`, not
  // the parent client/vendor — or the project predates ParentId being
  // backfilled) has no visible parent to nest under; that project is
  // collected into `looseProjects` instead and surfaced as a flat,
  // standalone entry (like a company) rather than silently dropped.
  const employersById = new Map(employers.map((e) => [e.id, e]));
  const looseProjects: Employer[] = [];
  const projectsByParent = new Map<string, Employer[]>();
  for (const e of employers) {
    if (e.kind !== 'OS_PROJECT') continue;
    const parent = e.parentId ? employersById.get(e.parentId) : undefined;
    if (parent && visible(parent)) {
      const list = projectsByParent.get(parent.id) ?? [];
      list.push(e);
      projectsByParent.set(parent.id, list);
    } else if (visible(e)) {
      looseProjects.push(e);
    }
  }

  // 3. Counterparties — BOTH Clients (OS_CLIENT) and Vendors (OS_VENDOR),
  //    each mapped to entries carrying their visible projects.
  const toEntries = (kind: 'OS_CLIENT' | 'OS_VENDOR'): CounterpartyEntry[] =>
    employers
      .filter((e) => e.kind === kind && visible(e))
      .map((employer) => ({
        employer,
        projects: projectsByParent.get(employer.id) ?? [],
      }));

  const clients = toEntries('OS_CLIENT');
  const vendors = toEntries('OS_VENDOR');

  // Place each counterparty under the COMPANY its ParentId references. Companies
  // are the tenant employer + managed COMPANY employers; a counterparty whose
  // ParentId is missing or points outside the visible company set falls back to
  // the tenant employer so it is never dropped.
  const companyIds = new Set<string>(companies.map((c) => c.id));
  if (tenantEmployer) companyIds.add(tenantEmployer.id);

  const bucketByCompany = (entries: CounterpartyEntry[]) => {
    const bySubsidiary = new Map<string, CounterpartyEntry[]>();
    const underTenant: CounterpartyEntry[] = [];
    for (const entry of entries) {
      const pid = entry.employer.parentId;
      if (pid && pid !== tenantEmployer?.id && companyIds.has(pid)) {
        const list = bySubsidiary.get(pid) ?? [];
        list.push(entry);
        bySubsidiary.set(pid, list);
      } else {
        underTenant.push(entry);
      }
    }
    return { bySubsidiary, underTenant };
  };
  const clientBuckets = bucketByCompany(clients);
  const vendorBuckets = bucketByCompany(vendors);

  // One node per managed company, carrying its own counterparties. Stable-sort
  // companies that actually own counterparties first so they stay visible above
  // the "Show more" fold.
  const subsidiaryNodes: CompanyNode[] = companies
    .map((employer) => ({
      employer,
      clients: clientBuckets.bySubsidiary.get(employer.id) ?? [],
      vendors: vendorBuckets.bySubsidiary.get(employer.id) ?? [],
    }))
    .sort((a, b) => {
      const aEmpty = a.clients.length + a.vendors.length > 0 ? 0 : 1;
      const bEmpty = b.clients.length + b.vendors.length > 0 ? 0 : 1;
      return aEmpty - bEmpty;
    });

  return {
    tenantEmployer,
    companies,
    companiesIndented: tenantEmployer !== null,
    clients,
    vendors,
    subsidiaryNodes,
    tenantClients: clientBuckets.underTenant,
    tenantVendors: vendorBuckets.underTenant,
    looseProjects,
  };
}

/**
 * Flatten the role-derived groups into the deduplicated, ordered list of
 * selectable employers (tenant employer → companies → clients + their projects
 * → vendors + their projects). Mirrors the rendered order so "first accessible
 * scope" is deterministic and matches what the switcher shows.
 */
export function flattenScopeGroups(groups: ScopeListGroups): Employer[] {
  const seen = new Set<string>();
  const out: Employer[] = [];
  const push = (e: Employer | null) => {
    if (!e || seen.has(e.id)) return;
    seen.add(e.id);
    out.push(e);
  };
  const pushEntries = (entries: CounterpartyEntry[]) => {
    for (const entry of entries) {
      push(entry.employer);
      for (const p of entry.projects) push(p);
    }
  };
  push(groups.tenantEmployer);
  for (const c of groups.companies) push(c);
  pushEntries(groups.clients);
  pushEntries(groups.vendors);
  for (const p of groups.looseProjects) push(p);
  return out;
}

/**
 * Compute the default scope for a principal:
 *  - tenant admins → global (All Scopes);
 *  - everyone else → the first accessible employer scope, if any (which, for a
 *    restricted user, is the first administered company in rendered order).
 * Returns `null` when there is nothing to pick (no employers + no admin role).
 */
export function defaultScopeFor(admin: boolean, flattened: Employer[]): ActiveScope | null {
  if (admin) return { scope: 'global' };
  const first = flattened[0];
  return first ? { scope: 'employer', employerId: first.id, kind: first.kind } : null;
}

export function useScopeTree() {
  const auth = useAuthStore();
  const queryClient = useQueryClient();
  const visitedRoutes = useVisitedRoutesStore();
  const { claims, isAuthenticated, activeScope } = storeToRefs(auth);

  // The employer list is tenant-wide; key it by the tenant id so switching
  // scope never refetches it. Falls back to a stable literal when the claim is
  // absent so the key stays defined while enabled is false.
  const tenantKey = computed<string>(() => claims.value?.['custom:tenant_id'] ?? '∅');

  const query = useQuery<Employer[], Error>({
    queryKey: ['scope-employers', tenantKey],
    queryFn: () => listEmployers(),
    enabled: computed(() => isAuthenticated.value),
    staleTime: 60_000,
  });

  const employers = computed<Employer[]>(() => query.data.value ?? EMPTY_EMPLOYERS);

  /** The role-derived, indented group structure the switcher renders. */
  const groups = computed<ScopeListGroups>(() =>
    deriveScopeListGroups(employers.value, claims.value),
  );

  /**
   * The ordered, deduplicated list of every employer the principal may select,
   * post role-filter. Drives default-pick + membership checks; ordering matches
   * the rendered group order so "first accessible scope" is deterministic.
   */
  const flattened = computed<Employer[]>(() => flattenScopeGroups(groups.value));

  const admin = computed(() => isTenantAdmin(claims.value));
  /** Top-level admins may select the global "All Scopes" view. */
  const canSelectGlobal = admin;

  /** The resolved `TENANT_EMPLOYER` id — used to scope per-module data under global. */
  const tenantEmployerId = computed<string | null>(
    () => findTenantEmployer(employers.value)?.id ?? null,
  );

  // Push the resolved tenant-employer id into the store so `activeEmployerId`
  // can bind per-module data to the tenant company while in global scope.
  watch(tenantEmployerId, (id) => auth.setTenantEmployerId(id), { immediate: true });

  // Tell the store while the list is still loading, so `activeEmployerId` does
  // not fall back to the (differently-formed) JWT claim id first and then flip
  // to the list's id — that flip made every employer-scoped query fire twice
  // (audit §2d). Settled (data or error) → the claim fallback is allowed again.
  const listPending = computed<boolean>(
    () => isAuthenticated.value && query.data.value === undefined && !query.isError.value,
  );
  watch(listPending, (pending) => auth.setEmployerListPending(pending), { immediate: true });

  // Default-pick: once the list resolves (or the admin role is known), seed the
  // scope if the user hasn't chosen one yet. Admins default to global; others
  // to their first administered company.
  watch(
    [flattened, admin, activeScope, isAuthenticated],
    ([list, isAdmin, current, authed]) => {
      if (!authed) return;
      if (current) return; // user (or a prior session) already has a scope
      if (!isAdmin && list.length === 0) return; // nothing to pick yet
      const pick = defaultScopeFor(isAdmin, list);
      if (pick) auth.setScope(pick);
    },
    { immediate: true },
  );

  /**
   * Rebind after a scope change: invalidate every scope-bound query (the active
   * screen's observer refetches immediately) and warm-prefetch the visited but
   * unmounted screens in the background so switching back is instant.
   */
  function rebindScope(): void {
    invalidateScopeBoundQueries(queryClient);
    prefetchVisitedScopes(queryClient, visitedRoutes.list());
  }

  /** Select a single employer scope + rebind all module queries. */
  function selectEmployerScope(employer: Pick<Employer, 'id' | 'kind'>): void {
    auth.selectEmployer(employer.id, employer.kind);
    rebindScope();
  }

  /** Switch to the cross-scope global view + rebind all module queries. */
  function selectGlobal(): void {
    auth.selectGlobalScope();
    rebindScope();
  }

  /**
   * Select a COMPANY's Direct Workforce (proto `setScope('direct', …)`) +
   * rebind all module queries.
   */
  function selectDirect(company: Pick<Employer, 'id'>): void {
    auth.selectDirectScope(company.id);
    rebindScope();
  }

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    employers,
    groups,
    flattened,
    canSelectGlobal,
    tenantEmployerId,
    selectEmployerScope,
    selectGlobal,
    selectDirect,
  };
}
